import React, { Fragment, useMemo } from "react";
import { useTable, usePagination, useExpanded, useSortBy } from "react-table";
import ReactLoading from 'react-loading';
import { BiChevronsLeft, BiChevronLeft, BiChevronsRight, BiChevronRight } from 'react-icons/bi'

import { t } from "i18next";
import PropTypes from 'prop-types';

const TableComponent = ({
  columns,
  data,
  fetchData,
  pageCount: controlledPageCount,
  loading,
  isPaginated = true,
  ...props
}) => {
  const defaultColumn = useMemo(
    () => ({
      // minWidth: 20,
      // maxWidth: 115
    }),
    []
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: 0,
        pageSize: 20,
        hiddenColumns: columns
          .filter((column) => !column.show)
          .map((column) => column.id),
      },
      manualPagination: true,
      manualSortBy: true,
      autoResetPage: false,
      pageCount: controlledPageCount,
    },
    useSortBy,
    useExpanded,
    usePagination
  );

  React.useEffect(() => {
    fetchData?.({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);


  return (
    <Fragment>
      {loading ? (

        <div className="h-96">
          <ReactLoading type='bubbles' color='#3b82f6' height={56} width={56} >
          </ReactLoading>
        </div>
      ) : (
        <div className="overflow-auto w-full">

          <div className=" -my-2 py-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div className="align-middle inline-block min-w-full shadow sm:rounded-lg  "></div>
            <table
              className="overflow-x-auto min-w-full divide-y divide-gray-200 border"
              {...getTableProps()}
            >
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 capitalize tracking-wider"
                        {...column.getHeaderProps()}
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>


              < tbody
                className="overflow-x-scroll bg-white divide-y divide-gray-200"
                {...getTableBodyProps()}
              >
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td
                            className="px-6 py-4 whitespace-no-wrap text-sm leading-5 font-medium text-gray-600"
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>


            </table>


            {data.length < 1 && <div className="flex items-center justify-around text-sm   bg-white  w-full h-56">
              {t('table.no_data')}
            </div>}


            {Boolean(isPaginated)

              &&

              <TablePagination
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                nextPage={nextPage}
                previousPage={previousPage}
                gotoPage={gotoPage}
                pageCount={pageCount}
                setPageSize={setPageSize}
                pageSize={pageSize}
                pageOptions={pageOptions}
                pageIndex={pageIndex}
              />
            }
          </div>
        </div>
      )
      }
    </Fragment >
  );
};

TableComponent.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  fetchData: PropTypes.func,
  pageCount: PropTypes.number,
  loading: PropTypes.bool,
  isPaginated: PropTypes.bool,
};

export default TableComponent;




const TablePagination = (
  {
    gotoPage,
    setPageSize, pageSize,
    canPreviousPage, canNextPage,
    previousPage, nextPage,
    pageIndex, pageCount, pageOptions
  }
) => {

  return (


    <div className=" flex flex-row py-2 text-sm justify-between items-center flex-1">
      <div className="flex items-center ">
        <span className="mr-4">Page{" "}<strong> {pageIndex + 1} {t('pagination.of')} {pageOptions.length}</strong>   </span>
        {/* <select
                  className="mt-1 block h-10 text-xs rounded border-gray-300  focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  value={pageSize}
                  onChange={onChangeInSelect}
              >
                  {pagesOptions.map((p) => (
                      <option key={p.label} value={p.value} >
                          {t('actions.show')+p.label}
                      </option>
                  ))}
              </select> */}

      </div>

      <div className="text-lg text-gray-600 ">
        <button
          className="bg-gray-100 border  mr-2 p-2 rounded-sm hover:bg-gray-200"
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
        >
          <span className="sr-only">{t('pagination.first')}</span>
          <BiChevronsLeft />
        </button>

        <button
          className="bg-gray-100 border   mr-2 p-2 rounded-sm hover:bg-gray-200"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          <span className="sr-only">{t('pagination.previous')}</span>
          <BiChevronLeft />
        </button>

        <button className="bg-gray-100 border  mr-2 p-2 rounded-sm hover:bg-gray-200" onClick={nextPage} disabled={!canNextPage}>
          <span className="sr-only">{t('pagination.next')}</span>
          <BiChevronRight />
        </button>

        <button
          className="bg-gray-100 border   p-2 rounded-sm hover:bg-gray-200" disabled={!canNextPage}
          onClick={() => gotoPage(pageCount - 1)}
        >
          <span className="sr-only">{t('pagination.last')}</span>
          <BiChevronsRight />
        </button>
      </div>


      {/* <div>
              <input
                  type="number"
                  min={1}
                  className="p-0 text-xs w-16 rounded-md border-transparent appearance-none border border-gray-300 bg-white text-gray-700 placeholder-gray-400  focus:outline-none focus:ring-2 focus:ring-indigo-500-600 focus:border-transparent"
                  max={pageOptions.length}
                  defaultValue={pageIndex + 1}
                  onChange={onChangeInInput}
              />
          </div> */}

    </div>

  );
}

TablePagination.propTypes = {
  gotoPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  canPreviousPage: PropTypes.bool.isRequired,
  canNextPage: PropTypes.bool.isRequired,
  previousPage: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  pageIndex: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  pageOptions: PropTypes.array.isRequired,
};
